import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, CircleDollarSign, Video, Camera, Shield, Crown, User, Play, ShoppingBag, Palette, Download, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import SubscriberBadge from "@/components/SubscriberBadge";
import AppLayout from "@/components/AppLayout";
import LiveStreamSection from "@/components/LiveStreamSection";
import heroBg from "@/assets/hero-bg.jpg";
import { useGamification } from "@/hooks/useGamification";
import { Button } from "@/components/ui/button";
import { Coins, ChevronRight, Image as ImageIcon, Link2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { transformDriveUrl } from "@/lib/imageUtils";
import { useQuery } from "@tanstack/react-query";


// CyberButton Component - Futuristic/Acid Graphic Style
const CyberButton = ({
  icon: Icon,
  label,
  onClick,
  accentColor = "#ccff00",
  code = "88301",
  badge,
  badgeIcon: BadgeIcon,
  extraIconProps = {}
}: any) => {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      style={{ '--accent': accentColor } as React.CSSProperties}
      className="group shrink-0 snap-start flex flex-col items-center justify-center gap-2 md:gap-3 bg-[#050505] border border-white/10 rounded-sm p-4 transition-all text-center relative overflow-hidden aspect-[9/16] w-[110px] md:w-[150px] hover:border-[var(--accent)] shadow-2xl"
    >
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:10px_10px] pointer-events-none"></div>

      {/* Cyber Blur Accent */}
      <div className="absolute -top-6 -right-6 w-20 h-20 blur-[30px] rounded-full opacity-20 group-hover:opacity-70 transition-opacity pointer-events-none" style={{ backgroundColor: accentColor }}></div>

      {/* Top Details */}
      <div className="absolute top-3 left-3 flex flex-col gap-[3px]">
        <div className="w-5 h-[2px]" style={{ backgroundColor: accentColor }}></div>
        <div className="w-2.5 h-[2px]" style={{ backgroundColor: accentColor }}></div>
      </div>
      <span className="absolute top-2 right-2 text-[8px] font-mono tracking-widest text-white/30 group-hover:text-[var(--accent)] transition-colors">[SYS]</span>

      {/* Bottom Details */}
      <span className="absolute bottom-2 left-2 text-[8px] font-mono tracking-widest opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accentColor }}>{code}</span>
      <div className="absolute bottom-2 right-2 text-white/20 text-[10px] font-mono font-bold">+</div>

      {badge && BadgeIcon && (
        <div className="absolute top-6 right-2">
          <BadgeIcon className="w-3 h-3" style={{ color: accentColor }} />
        </div>
      )}

      {/* Icon and Text */}
      <Icon className="w-6 h-6 md:w-8 md:h-8 text-white group-hover:text-[var(--accent)] transition-colors shrink-0 relative z-10" {...extraIconProps} />
      <span className="font-display text-[10px] md:text-lg text-white/90 leading-tight uppercase font-black tracking-tighter relative z-10 group-hover:tracking-wider transition-all drop-shadow-md whitespace-pre-line mt-1">
        {label}
      </span>
    </motion.button>
  );
};

const Home = () => {
 const navigate = useNavigate();
 const { user, profile, isAdmin } = useAuth();
 const { isSubscriber, subscription, planId } = useSubscription();
 const [currentSlide, setCurrentSlide] = useState(0);
 const [isInstalled, setIsInstalled] = useState(false);
  const [showPartnerWelcome, setShowPartnerWelcome] = useState(false);
  const [showPartnerBonus, setShowPartnerBonus] = useState(false);
  const [partnerBonusAmount, setPartnerBonusAmount] = useState(0);
  const [partnerData, setPartnerData] = useState<any>(null);

  useEffect(() => {
    const checkPartnerWelcome = async () => {
      if (!user?.id) return;
      const { data: partner, error } = await supabase.from('partners').select('*').eq('user_id', user.id).maybeSingle();
      
      if (partner && !error) {
        setPartnerData(partner);
        
        const partnerCreatedAt = new Date(partner.created_at || Date.now());
        const now = new Date();
        const diffHours = Math.abs(now.getTime() - partnerCreatedAt.getTime()) / 36e5;
        
        const hasSeenLocal = localStorage.getItem(`partner_welcome_${partner.id}`);
        // É considerado novo parceiro se nunca viu o aviso (local) E a conta tem menos de 24 horas
        const isNewPartner = !hasSeenLocal && diffHours < 24;

        if (isNewPartner) {
          setShowPartnerWelcome(true);
        } else {
          if (!hasSeenLocal) {
            localStorage.setItem(`partner_welcome_${partner.id}`, "true");
          }
          const hasSeenBonusThisSession = sessionStorage.getItem(`partner_bonus_seen_${partner.id}`);
          if (!hasSeenBonusThisSession) {
            const { data: comms } = await supabase.from('partner_commissions').select('commission_amount').eq('partner_id', partner.id);
            const total = (comms || []).reduce((acc, curr) => acc + Number(curr.commission_amount), 0);
            setPartnerBonusAmount(total);
            setShowPartnerBonus(true);
          }
        }
      }
    };
    checkPartnerWelcome();
  }, [user]);

  const handleClosePartnerWelcome = () => {
    if (partnerData) {
      localStorage.setItem(`partner_welcome_${partnerData.id}`, "true");
      sessionStorage.setItem(`partner_bonus_seen_${partnerData.id}`, "true");
    }
    setShowPartnerWelcome(false);
    navigate('/profile?tab=partner');
  };

  const handleClosePartnerBonus = () => {
    if (partnerData) {
      sessionStorage.setItem(`partner_bonus_seen_${partnerData.id}`, "true");
    }
    setShowPartnerBonus(false);
  };

 useEffect(() => {
 const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
 setIsInstalled(standalone);
 }, []);

 const { data: homeData, isLoading: loadingAlbums } = useQuery({
 queryKey: ['homeData', profile?.id, isAdmin],
 queryFn: async () => {
 // Base query for public albums
 let albumsQuery = supabase
 .from("albums")
 .select("*")
 .eq("published", true)
 .eq("is_visible", true);

 // If user is not admin, we handle public/private/restricted logic
 if (!isAdmin) {
 const { data: taggedAlbumIds } = profile?.user_id ? await supabase
 .from("album_players")
 .select("album_id")
 .eq("player_id", profile.user_id) : { data: [] };
 
 const taggedIds = taggedAlbumIds?.map(a => a.album_id) || [];
 
 const { data: authorizedAlbumIds } = profile?.user_id ? await supabase
 .from("album_permissions" as any)
 .select("album_id")
 .eq("user_id", profile.user_id)
 .eq("status", "approved") : { data: [] };
 
 const authorizedIds = authorizedAlbumIds?.map(a => a.album_id) || [];
 const allPermittedIds = [...new Set([...taggedIds, ...authorizedIds])];

 if (allPermittedIds.length > 0) {
 albumsQuery = albumsQuery.or(`is_public.eq.true,restricted_access.eq.true,id.in.(${allPermittedIds.join(',')})`);
 } else {
 albumsQuery = albumsQuery.or(`is_public.eq.true,restricted_access.eq.true`);
 }
 }

 const [{ data: settings }, { data: albums }, { data: products }, { data: templates }] = await Promise.all([
 supabase.from("site_settings" as any).select("*") as any,
 albumsQuery.order("created_at", { ascending: false }).limit(4),
 supabase.from("store_products" as any).select("*").eq("is_active", true).order("created_at", { ascending: false }).limit(8),
 supabase.from("designer_templates" as any).select("*").order("created_at", { ascending: false }).limit(4)
 ]);
 
 let parsedSlides = [];
 let parsedShowLive = true;
 let parsedShowDesigner = true;

 if (settings && Array.isArray(settings)) {
 const slidesSet = settings.find((s: any) => s.key === "home_hero_slides");
 if (slidesSet?.value) parsedSlides = JSON.parse(slidesSet.value);
 
 parsedShowLive = settings.find((s: any) => s.key === "home_show_live")?.value !== "false";
 parsedShowDesigner = settings.find((s: any) => s.key === "home_show_designer")?.value !== "false";
 }

 return {
 slides: parsedSlides,
 showLive: parsedShowLive,
 showDesigner: parsedShowDesigner,
 recentAlbums: albums || [],
 storeProducts: products || [],
 designerTemplates: templates || []
 };
 },
 staleTime: 1000 * 60 * 5, // Cache for 5 minutes
 });

 const slides = homeData?.slides || [];
 const recentAlbums = homeData?.recentAlbums || [];
 const storeProducts = homeData?.storeProducts || [];
 const showLive = homeData?.showLive ?? true;
 const showDesigner = homeData?.showDesigner ?? true;
 const designerTemplates = homeData?.designerTemplates || [];

 useEffect(() => {
 if (slides.length <= 1) return;
 const interval = setInterval(() => {
 setCurrentSlide((prev) => (prev + 1) % slides.length);
 }, 5000);
 return () => clearInterval(interval);
 }, [slides]);

  if (profile?.is_hudy_motion) {
    return (
      <AppLayout activeNav="Início">
        <div className="relative h-[40vh] overflow-hidden rounded-b-[40px] shadow-2xl">
          <img src={heroBg} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
             <h1 className="text-4xl font-black mb-2">Bem-vindo ao <span className="text-primary">Hudy Motion</span></h1>
             <p className="text-muted-foreground">O seu estúdio de hotsites e animações.</p>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/motion")}
              className="flex flex-col items-center justify-center bg-black/80 border border-red-500/20 rounded-3xl p-6 transition-all text-center min-h-[160px] h-full overflow-hidden shadow-2xl shadow-red-500/10"
            >
              <img src="/HUDY-MOTION.png" alt="Hudy Motion" className="w-[80%] h-20 object-contain drop-shadow-[0_0_15px_rgba(255,0,0,0.2)] mb-4" />
              <span className="font-bold text-muted-foreground uppercase tracking-widest text-xs">Criar Hotsite Animado</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/designer")}
              className="flex flex-col items-center justify-center bg-primary/10 border border-primary/20 rounded-3xl p-6 transition-all text-center min-h-[160px] h-full overflow-hidden shadow-2xl shadow-primary/10"
            >
              <Palette className="w-12 h-12 text-primary mb-4" />
              <span className="font-bold text-foreground uppercase tracking-widest text-lg">Designer</span>
              <span className="font-bold text-muted-foreground uppercase tracking-widest text-xs mt-1">Vitrine de Artes</span>
            </motion.button>
          </div>
        </div>
      </AppLayout>
    );
  }

 return (
 <AppLayout activeNav="Início">
 {/* Hero */}
 <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
  <AnimatePresence mode="wait">
    <motion.img
      key={slides[currentSlide] || "default"}
      initial={{ opacity: 0, x: -20, filter: "contrast(3) brightness(2) hue-rotate(-90deg)" }}
      animate={{ 
        opacity: [0, 1, 1, 1, 1], 
        x: [20, -15, 10, -5, 0], 
        filter: [
          "contrast(3) brightness(2) hue-rotate(-90deg) saturate(3)", 
          "contrast(1) brightness(0.5) hue-rotate(90deg) saturate(0)", 
          "contrast(2) brightness(2) hue-rotate(45deg) saturate(2)", 
          "contrast(1.5) brightness(1.2) hue-rotate(-20deg) saturate(1.5)", 
          "contrast(1) brightness(1) hue-rotate(0deg) saturate(1)"
        ],
        clipPath: [
          "inset(20% 0 60% 0)",
          "inset(70% 0 10% 0)",
          "inset(40% 0 30% 0)",
          "inset(0% 0 0% 0)",
          "inset(0% 0 0% 0)"
        ]
      }}
      exit={{ opacity: 0, filter: "brightness(3) blur(20px)", scale: 1.1 }}
      transition={{ duration: 0.5, times: [0, 0.1, 0.2, 0.3, 1], ease: "anticipate" }}
      src={slides[currentSlide] || heroBg}
      alt="Futebol"
      loading="eager"
      decoding="async"
      fetchPriority="high"
      className="w-full h-full object-cover"
    />
    
    {/* Efeito de Ruído TV e Luzes (Glitz) */}
    <motion.div
      key={`noise-${slides[currentSlide] || "default"}`}
      className="absolute inset-0 pointer-events-none mix-blend-screen z-10"
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0, 0.8, 1, 0.4, 0],
        backgroundImage: [
          "none",
          "url('data:image/svg+xml;utf8,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22 opacity=%220.5%22/%3E%3C/svg%3E')",
          "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)",
          "url('data:image/svg+xml;utf8,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22 opacity=%220.5%22/%3E%3C/svg%3E')",
          "none"
        ],
        backgroundSize: ["auto", "100px 100px", "100% 200%", "100px 100px", "auto"],
        backgroundPosition: ["0 0", "0 0", "0 100%", "0 0", "0 0"]
      }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, times: [0, 0.1, 0.2, 0.3, 1] }}
    />
  </AnimatePresence>
 <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
 <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
 {/* Alerta de Assinatura Legada (Apenas para quem já é VIP mercado_pago, mas não é recorrente) */}
 {isSubscriber && subscription?.payment_provider === 'mercado_pago' && !subscription?.is_recurring && (
 <motion.div 
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 className="mb-4 bg-primary/20 backdrop-blur-xl border border-primary/30 p-3 rounded-xl flex items-center justify-between gap-4 max-w-xl group cursor-pointer hover:bg-primary/30 transition-all border-l-4 border-l-primary"
 onClick={() => navigate("/subscription")}
 >
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center animate-pulse">
 <Crown className="w-4 h-4 text-black" />
 </div>
 <div>
 <p className="text-[10px] font-black text-primary uppercase tracking-widest ">Atenção Assinante VIP</p>
 <p className="text-[11px] text-foreground font-medium leading-tight">Sua renovação automática está desativada. <strong className="text-primary">Clique aqui para ativar</strong> e não perder seus descontos!</p>
 </div>
 </div>
 <ChevronRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
 </motion.div>
 )}

 {/* Alerta de Assinatura Suspensa (PIX Recorrente ou Cartão) */}
 {subscription?.status === 'suspended' && (
 <motion.div 
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 className="mb-4 bg-red-500/20 backdrop-blur-xl border border-red-500/30 p-3 rounded-xl flex items-center justify-between gap-4 max-w-xl group cursor-pointer hover:bg-red-500/30 transition-all border-l-4 border-l-red-500"
 onClick={() => navigate("/subscription")}
 >
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center animate-pulse">
 <Shield className="w-4 h-4 text-foreground" />
 </div>
 <div>
 <p className="text-[10px] font-black text-red-400 uppercase tracking-widest ">Assinatura Suspensa</p>
 <p className="text-[11px] text-foreground font-medium leading-tight">
 Sua assinatura está suspensa por falta de saldo em sua conta. <strong className="text-red-400">Por favor verifique sua conta</strong> e usufrua dos benefícios de assinante!
 </p>
 </div>
 </div>
 <ChevronRight className="w-4 h-4 text-red-500 group-hover:translate-x-1 transition-transform" />
 </motion.div>
 )}

 <motion.div
 initial={{ opacity: 0, y: 30 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.8 }}
 >
 <h1 className="text-4xl md:text-6xl font-display text-gradient tracking-wider mb-2 uppercase leading-tight font-black">
 By Hudson Raone
 </h1>
 <p className="text-foreground/80 text-sm md:text-base max-w-lg mb-5 flex items-center gap-2">
 {profile?.full_name ? `Bem-vindo, ${profile.full_name}!` : "Bem-vindo!"}{" "}
 {isSubscriber && <SubscriberBadge planId={planId} />}
 <span className="text-xs text-muted-foreground">
 Produção audiovisual profissional para seus melhores momentos.
 </span>
 </p>
 <div className="flex gap-3 flex-wrap">
 {profile?.is_player && (
 <button
 onClick={() => navigate("/my-records")}
 className="inline-flex items-center gap-2 bg-gradient-primary text-primary-foreground font-semibold glow px-4 py-2.5 rounded-md text-sm transition-opacity hover:opacity-90"
 >
 <Camera className="w-4 h-4" /> Meus Registros
 </button>
 )}
 {!profile?.is_player && (
 <button
 onClick={() => navigate("/profile")}
 className="inline-flex items-center gap-2 border border-primary/30 text-primary px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-primary/10 transition-colors"
 >
 <Shield className="w-4 h-4" /> Ativar Modo Jogador
 </button>
 )}
 {!isSubscriber && (
 <button
 onClick={() => navigate("/subscription")}
 className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold px-4 py-2.5 rounded-md text-sm transition-opacity hover:opacity-90"
 >
 <Crown className="w-4 h-4" /> Tornar-se Assinante
 </button>
 )}
 </div>
 </motion.div>
 </div>
 </div>

 {/* Quick action buttons - Responsive Grid (Cyberpunk Futuristic Style) */}
<section className="px-4 md:px-8 py-6 relative">
  {/* Fundo Decorativo Tecnológico */}
  <div className="absolute left-0 top-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
  <div className="absolute left-0 bottom-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

  <div className="flex gap-2 md:gap-4 overflow-x-auto pb-6 snap-x snap-mandatory hide-scrollbar">
    <CyberButton
      icon={ShoppingBag}
      label="Loja"
      onClick={() => navigate("/store")}
      accentColor="#ccff00"
      code="88301"
    />

    {profile?.is_player && (
      <>
        <CyberButton
          icon={Camera}
          label={"Meus\nRegistros"}
          onClick={() => navigate("/my-records")}
          accentColor="#00ffcc"
          code="REC-09"
        />
        <CyberButton
          icon={Play}
          label={"Meus\nLances"}
          onClick={() => navigate("/my-plays")}
          accentColor="#ff00cc"
          code="PLY-X"
        />
      </>
    )}

    <CyberButton
      icon={Trophy}
      label="Jogos"
      onClick={() => navigate("/jogos")}
      accentColor="#ccff00"
      code="EVT-01"
    />

    <CyberButton
      icon={CircleDollarSign}
      label={"Racha\nPIX"}
      onClick={() => navigate('/sessoes')}
      accentColor="#FF6A00"
      code="RCH-PX"
    />

    <CyberButton
      icon={Coins}
      label={"Minhas\nCompras"}
      onClick={() => navigate("/my-purchases")}
      accentColor="#ccff00"
      code="PAY-77"
    />

    <CyberButton
      icon={Palette}
      label="Designer"
      onClick={() => navigate("/designer")}
      accentColor="#ccff00"
      code="DSG-00"
      badge={true}
      badgeIcon={Crown}
    />

    <CyberButton
      icon={Play}
      label={"Hudy\nMotion"}
      onClick={() => navigate("/motion")}
      accentColor="#ccff00"
      code="MOT-FX"
    />

    <CyberButton
      icon={Link2}
      label="Futlink"
      onClick={() => isAdmin ? navigate("/admin#futlink") : window.open("/futlink", "_blank")}
      accentColor="#ccff00"
      code="LNK-99"
    />

    {isAdmin && (
      <CyberButton
        icon={Shield}
        label={"Painel\nAdmin"}
        onClick={() => navigate("/admin")}
        accentColor="#ff0055"
        code="ADM-XX"
      />
    )}

    {!isInstalled && (
      <CyberButton
        icon={Download}
        label={"Instalar\nApp"}
        onClick={() => {
          localStorage.removeItem("pwa-install-dismissed");
          window.dispatchEvent(new Event("force-pwa-prompt"));
        }}
        accentColor="#00ffaa"
        code="APP-DL"
        extraIconProps={{ className: "w-6 h-6 md:w-8 md:h-8 text-white group-hover:text-[var(--accent)] transition-colors shrink-0 relative z-10 animate-bounce" }}
      />
    )}
  </div>
</section>

{/* Live Stream Section */}
 {showLive && <LiveStreamSection />}

 {/* Designer Preview Section — NEW */}
 {showDesigner && designerTemplates.length > 0 && (
 <section className="px-4 md:px-8 py-10">
 <div className="flex items-center justify-between mb-6">
 <div>
 <h2 className="text-xl md:text-3xl font-display font-black text-foreground  tracking-tighter uppercase">Designer & Artes</h2>
 <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-1">Modelos exclusivos para suas redes sociais</p>
 </div>
 <Button 
 variant="ghost" 
 onClick={() => navigate("/designer")}
 className="text-primary hover:text-primary/80 font-black text-[10px] uppercase tracking-widest gap-2"
 >
 Ver Modelos <ChevronRight className="w-4 h-4" />
 </Button>
 </div>

 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {designerTemplates.map((template) => (
 <motion.div
 key={template.id}
 whileHover={{ y: -5 }}
 onClick={() => navigate("/designer")}
 className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-card/40 border border-foreground/10 cursor-pointer shadow-xl"
 >
 <img src={template.preview_url} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={template.name} />
 <div className="absolute top-3 right-3 z-20 bg-foreground/10 backdrop-blur-md px-2 py-1 rounded text-[8px] font-black uppercase tracking-widest text-foreground/40 ">9:16</div>
 <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
 <div className="absolute bottom-0 left-0 right-0 p-4">
 <p className="text-[10px] font-black text-primary uppercase tracking-widest  mb-1">{template.category}</p>
 <h3 className="text-sm font-display font-black text-foreground  tracking-tighter uppercase leading-tight line-clamp-1">{template.name}</h3>
 </div>
 </motion.div>
 ))}
 </div>
 </section>
 )}

 {/* Recent Albums Section — NEW */}
 <section className="px-4 md:px-8 py-10 mb-20">
 <div className="flex items-center justify-between mb-6">
 <div>
 <h2 className="text-xl md:text-3xl font-display font-black text-foreground  tracking-tighter uppercase">Galerias Recentes</h2>
 <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-1">Últimas publicações de Hudson Raone</p>
 </div>
 <Button 
 variant="ghost" 
 onClick={() => navigate("/albums")}
 className="text-primary hover:text-primary/80 font-black text-[10px] uppercase tracking-widest gap-2"
 >
 Ver Tudo <ChevronRight className="w-4 h-4" />
 </Button>
 </div>

 {loadingAlbums ? (
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {[...Array(4)].map((_, i) => (
 <div key={i} className="aspect-[4/5] bg-foreground/10 rounded-2xl animate-pulse" />
 ))}
 </div>
 ) : recentAlbums.length > 0 ? (
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {recentAlbums.map((album) => (
 <motion.div
 key={album.id}
 whileHover={{ y: -5 }}
 onClick={() => navigate(`/albums/${album.id}`)}
 className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-secondary border border-foreground/10 cursor-pointer shadow-xl"
 >
 {album.cover_image_url ? (
 <img src={transformDriveUrl(album.cover_image_url)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={album.name} />
 ) : (
 <div className="w-full h-full flex items-center justify-center opacity-20">
 <ImageIcon className="w-12 h-12" />
 </div>
 )}
 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
 <div className="absolute bottom-0 left-0 right-0 p-4">
 <div className="flex items-center justify-between mb-1">
 <p className="text-[10px] font-black text-primary uppercase tracking-widest  opacity-80">{album.album_type || "Evento"}</p>
 {album.restricted_access && <Shield className="w-3 h-3 text-primary animate-pulse" />}
 </div>
 <h3 className="text-sm md:text-base font-display font-black text-foreground  tracking-tighter uppercase leading-tight line-clamp-2">{album.name}</h3>
 </div>
 </motion.div>
 ))}
 </div>
 ) : (
 <div className="py-12 bg-foreground/10 rounded-2xl border border-dashed border-foreground/10 flex flex-col items-center justify-center text-center">
 <ImageIcon className="w-8 h-8 text-muted-foreground mb-3 opacity-20" />
 <p className="text-xs font-black text-muted-foreground uppercase tracking-widest">Nenhuma galeria pública encontrada.</p>
 </div>
 )}
 </section>

 {/* Store Section — NEW */}
 {storeProducts.length > 0 && (
 <section className="px-4 md:px-8 py-10 mb-20 bg-foreground/10 border-t border-foreground/10 relative overflow-hidden">
 <div className="absolute top-0 right-0 p-32 bg-primary/5 blur-[100px] rounded-full pointer-events-none" />
 <div className="flex items-center justify-between mb-8 relative z-10">
 <div>
 <h2 className="text-xl md:text-3xl font-display font-black text-foreground  tracking-tighter uppercase flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate("/store")}>
 <ShoppingBag className="w-6 h-6 md:w-8 md:h-8 text-primary" /> 
 <span>LOJA</span>
 </h2>
 <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-1">Produtos Exclusivos</p>
 </div>
 <Button 
 variant="ghost" 
 onClick={() => navigate("/store")}
 className="text-primary hover:text-primary/80 font-black text-[10px] uppercase tracking-widest gap-2"
 >
 Visitar Loja <ChevronRight className="w-4 h-4" />
 </Button>
 </div>

 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
 {storeProducts.slice(0, 4).map((p) => (
 <motion.div
 key={p.id}
 whileHover={{ y: -5 }}
 onClick={() => navigate("/store")}
 className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-card/40 backdrop-blur-md border border-foreground/10 cursor-pointer shadow-xl flex flex-col items-center justify-center p-4 text-center"
 >
 <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-90 z-10" />
 {p.image_url ? (
 <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
 ) : (
 <ShoppingBag className="w-12 h-12 text-muted-foreground opacity-20 absolute" />
 )}
 
 {/* Categoria flutuante */}
 <div className="absolute top-3 right-3 z-20 bg-foreground/10 backdrop-blur-md border border-foreground/10 px-3 py-1 rounded-full">
 <span className="text-[9px] font-black text-primary uppercase tracking-widest ">{p.category}</span>
 </div>

 <div className="relative z-20 mt-auto w-full pt-4 border-t border-foreground/10 text-left">
 <h3 className="text-sm font-display font-black text-foreground  tracking-tighter uppercase leading-tight line-clamp-1">{p.name}</h3>
 <p className="text-xs font-black text-primary mt-1">R$ {p.price.toFixed(0)}</p>
 </div>
 </motion.div>
 ))}
 </div>
 </section>
 )}
 
      
      {/* PARTNER WELCOME MODAL */}
      <AnimatePresence>
        {showPartnerWelcome && (
          <motion.div 
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(10px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
          >
            {/* Elementos gamer no fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/20 rounded-full blur-[120px] animate-pulse" />
              <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }} />
            </div>

            <motion.div 
              initial={{ scale: 0.8, y: 50, rotateX: 20 }}
              animate={{ scale: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.8, y: 50, rotateX: -20 }}
              transition={{ type: "spring", damping: 20, stiffness: 100 }}
              style={{ perspective: "1000px" }}
              className="bg-zinc-950 border-2 border-emerald-500/50 rounded-2xl p-1 max-w-md w-full relative shadow-[0_0_80px_rgba(16,185,129,0.3)] text-center group"
            >
              {/* Borda Neon Animada */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500 via-transparent to-amber-500 opacity-20 group-hover:opacity-40 transition-opacity duration-1000" />
              
              <div className="bg-[#0a0a0c] rounded-xl p-8 relative overflow-hidden z-10 h-full w-full">
                {/* Linhas de Grade Estilo Cyberpunk */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#10b981 1px, transparent 1px), linear-gradient(90deg, #10b981 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

                <div className="relative mx-auto w-24 h-24 mb-6">
                  {/* Glow do Avatar */}
                  <div className="absolute inset-0 bg-emerald-500/40 rounded-full blur-xl animate-pulse" />
                  
                  {/* Avatar Hexagonal / Redondo Gamer */}
                  <div className="w-24 h-24 rounded-full border-4 border-emerald-400 overflow-hidden relative z-10 shadow-[0_0_30px_rgba(16,185,129,0.5)]">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                        <User className="w-10 h-10 text-emerald-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Tag VIP/Parceiro absolute */}
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-emerald-400 text-black text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-sm shadow-[0_0_15px_rgba(16,185,129,0.8)] z-20 whitespace-nowrap">
                    Oficial
                  </div>
                </div>

                <motion.h2 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-3xl font-display font-black text-white mb-2 tracking-tighter uppercase drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]"
                >
                  Novo Parceiro!
                </motion.h2>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-foreground/70 mb-8 text-xs font-bold uppercase tracking-widest"
                >
                  Bem-vindo ao esquadrão <strong className="text-emerald-400 drop-shadow-[0_0_5px_rgba(16,185,129,0.8)]">VIP</strong>
                </motion.p>
                
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-black/60 border border-emerald-500/20 rounded-xl p-5 mb-8 text-left relative overflow-hidden group-hover:border-emerald-500/40 transition-colors"
                >
                  <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 blur-xl rounded-full" />
                  
                  <p className="text-[10px] text-emerald-400 uppercase tracking-[0.2em] font-black mb-3 flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> Benefício Desbloqueado
                  </p>
                  <p className="text-sm text-foreground/90 font-medium leading-relaxed">
                    Acompanhe suas vendas, gerencie o cupom <strong className="text-emerald-400 tracking-widest bg-emerald-500/10 px-1 py-0.5 rounded border border-emerald-500/20">{partnerData?.coupon_code}</strong> e solicite <strong className="text-white">saques via PIX</strong> direto pelo seu novo painel!
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button 
                    onClick={handleClosePartnerWelcome}
                    className="w-full bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-black font-black uppercase tracking-[0.2em] h-14 rounded-xl text-xs transition-all shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)] hover:scale-[1.02] active:scale-95"
                  >
                    Acessar Meu Painel
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* PARTNER BONUS MODAL */}
        {showPartnerBonus && (
          <motion.div 
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(10px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
          >
            <motion.div 
              initial={{ scale: 0.8, y: 50, rotateX: 20 }}
              animate={{ scale: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.8, y: 50, rotateX: -20 }}
              transition={{ type: "spring", damping: 20, stiffness: 100 }}
              style={{ perspective: "1000px" }}
              className="bg-zinc-950 border-2 border-emerald-500/50 rounded-2xl p-1 max-w-md w-full relative shadow-[0_0_80px_rgba(16,185,129,0.3)] text-center group"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500 via-transparent to-amber-500 opacity-20 group-hover:opacity-40 transition-opacity duration-1000" />
              
              <div className="bg-[#0a0a0c] rounded-xl p-8 relative overflow-hidden z-10 h-full w-full">
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#10b981 1px, transparent 1px), linear-gradient(90deg, #10b981 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

                <div className="relative mx-auto w-24 h-24 mb-6">
                  <div className="absolute inset-0 bg-emerald-500/40 rounded-full blur-xl animate-pulse" />
                  <div className="w-24 h-24 rounded-full border-4 border-emerald-400 overflow-hidden relative z-10 shadow-[0_0_30px_rgba(16,185,129,0.5)]">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                        <User className="w-10 h-10 text-emerald-400" />
                      </div>
                    )}
                  </div>
                </div>

                <motion.h2 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-3xl font-display font-black text-white mb-2 tracking-tighter uppercase drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]"
                >
                  Resumo de Ganhos
                </motion.h2>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-foreground/70 mb-8 text-xs font-bold uppercase tracking-widest"
                >
                  Bom ver você de volta!
                </motion.p>
                
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-black/60 border border-emerald-500/20 rounded-xl p-5 mb-8 text-center relative overflow-hidden group-hover:border-emerald-500/40 transition-colors"
                >
                  <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/10 blur-xl rounded-full" />
                  
                  <p className="text-[10px] text-emerald-400 uppercase tracking-[0.2em] font-black mb-2 flex items-center justify-center gap-2">
                    <Trophy className="w-4 h-4" /> Bônus Acumulado
                  </p>
                  <p className="text-4xl text-white font-black tracking-tighter">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(partnerBonusAmount)}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex gap-3"
                >
                  <Button 
                    variant="outline"
                    onClick={handleClosePartnerBonus}
                    className="flex-1 bg-transparent border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 font-bold uppercase tracking-widest h-14 rounded-xl text-xs transition-all"
                  >
                    Fechar
                  </Button>
                  <Button 
                    onClick={() => {
                      handleClosePartnerBonus();
                      navigate('/profile?tab=partner');
                    }}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-black font-black uppercase tracking-widest h-14 rounded-xl text-xs transition-all shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)] hover:scale-[1.02] active:scale-95"
                  >
                    Ver Painel
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
</AppLayout>
  );
};

export default Home;

